/**
 * Theme Toggle Component
 * Switch between light and dark themes with moon/sun icons
 * @component ThemeToggle
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import './style.css';

const ThemeToggle: React.FC = () => {
  const { colors, updateTheme } = useTheme();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Define dark theme colors
  const darkThemeColors = {
    navigationBg: '#1a1a1a',
    navigationText: '#ffffff',
    navigationAccent: '#4a5568',
    navigationHover: '#2d3748',
    navigationIconColor: '#ffffff',
    navigationMenuText: '#ffffff',
    headerBg: '#2d3748',
    headerText: '#ffffff',
    bg: '#1a202c',
    cardBg: '#2d3748',
    borderColor: '#4a5568',
    textColor: '#e2e8f0',
    textSecondary: '#a0aec0',
    primary: '#63b3ed',
    secondary: '#b794f4',
    success: '#68d391',
    error: '#fc8181',
    warning: '#f6ad55',
    info: '#4fd1c5',
    gray: '#718096',
  };

  // Define light theme colors (same as default)
  const lightThemeColors = {
    navigationBg: '#ffffff',
    navigationText: '#1f2d3d',
    navigationAccent: '#2c3e50',
    navigationHover: '#e5e7eb',
    navigationIconColor: '#1f2d3d',
    navigationMenuText: '#1f2d3d',
    headerBg: '#ffffff',
    headerText: '#1f2d3d',
    bg: '#f8f9fa',
    cardBg: '#ffffff',
    borderColor: '#e9ecef',
    textColor: '#212529',
    textSecondary: '#6c757d',
    primary: '#1976d2',
    secondary: '#9c27b0',
    success: '#4caf50',
    error: '#d32f2f',
    warning: '#ffa000',
    info: '#0288d1',
    gray: '#bdbdbd',
  };

  // Check current theme on mount
  useEffect(() => {
    const isDark = colors.bg === '#1a202c';
    setIsDarkMode(isDark);
    
    // Set data-theme attribute on both html and body
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    document.body.setAttribute('data-theme', isDark ? 'dark' : 'light');
    
    // Log current CSS variables
    const root = document.documentElement;
    const currentNavBg = getComputedStyle(root).getPropertyValue('--navigation-bg');
    const currentSidebarBg = getComputedStyle(root).getPropertyValue('--sidebar-bg');
    console.log('Current navigation bg:', currentNavBg);
    console.log('Current sidebar bg:', currentSidebarBg);
    console.log('Is dark mode:', isDark);
    
    // For non-authenticated users, check localStorage
    const savedTheme = localStorage.getItem('theme');
    console.log('Saved theme:', savedTheme, 'Current isDark:', isDark);
    
    if (savedTheme && savedTheme === 'dark' && !isDark) {
      // Apply dark theme from localStorage
      const root = document.documentElement;
      root.style.setProperty('--navigation-bg', darkThemeColors.navigationBg);
      root.style.setProperty('--navigation-text', darkThemeColors.navigationText);
      root.style.setProperty('--navigation-accent', darkThemeColors.navigationAccent);
      root.style.setProperty('--navigation-hover', darkThemeColors.navigationHover);
      root.style.setProperty('--navigation-icon-color', darkThemeColors.navigationIconColor);
      root.style.setProperty('--navigation-menu-text', darkThemeColors.navigationMenuText);
      root.style.setProperty('--header-bg', darkThemeColors.headerBg);
      root.style.setProperty('--header-text', darkThemeColors.headerText);
      root.style.setProperty('--bg', darkThemeColors.bg);
      root.style.setProperty('--card-bg', darkThemeColors.cardBg);
      root.style.setProperty('--border-color', darkThemeColors.borderColor);
      root.style.setProperty('--text-color', darkThemeColors.textColor);
      root.style.setProperty('--text-secondary', darkThemeColors.textSecondary);
      root.style.setProperty('--primary', darkThemeColors.primary);
      root.style.setProperty('--secondary', darkThemeColors.secondary);
      root.style.setProperty('--success', darkThemeColors.success);
      root.style.setProperty('--error', darkThemeColors.error);
      root.style.setProperty('--warning', darkThemeColors.warning);
      root.style.setProperty('--info', darkThemeColors.info);
      root.style.setProperty('--gray', darkThemeColors.gray);
      
      // Also set navigation-specific variables
      root.style.setProperty('--sidebar-bg', darkThemeColors.navigationBg);
      root.style.setProperty('--sidebar-border', darkThemeColors.borderColor);
      root.style.setProperty('--accent', darkThemeColors.navigationAccent);
      root.style.setProperty('--text', darkThemeColors.navigationText);
      root.style.setProperty('--sidebar-width', '280px');
      root.style.setProperty('--sidebar-collapsed-width', '80px');
      
      console.log('Applied dark theme from localStorage');
      setIsDarkMode(true);
      document.documentElement.setAttribute('data-theme', 'dark');
      document.body.setAttribute('data-theme', 'dark');
    }
  }, [colors]);

  const handleToggle = async () => {
    const newTheme = isDarkMode ? lightThemeColors : darkThemeColors;
    const newIsDark = !isDarkMode;
    
    try {
      // Try to update theme via API first
      await updateTheme(newTheme);
      console.log('Theme updated via API successfully');
      setIsDarkMode(newIsDark);
      
      // Update data-theme attribute on both html and body
      document.documentElement.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
      document.body.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
      
      // Save to localStorage for persistence
      localStorage.setItem('theme', newIsDark ? 'dark' : 'light');
      
      console.log('Theme switched to:', newIsDark ? 'dark' : 'light');
      console.log('Navigation bg should be:', newTheme.navigationBg);
    } catch (error) {
      // If user is not authenticated, just apply theme locally
      console.log('User not authenticated, applying theme locally');
      console.log('Setting theme colors:', newTheme);
      
      // Apply theme colors to CSS variables directly
      const root = document.documentElement;
      root.style.setProperty('--navigation-bg', newTheme.navigationBg);
      root.style.setProperty('--navigation-text', newTheme.navigationText);
      root.style.setProperty('--navigation-accent', newTheme.navigationAccent);
      root.style.setProperty('--navigation-hover', newTheme.navigationHover);
      root.style.setProperty('--navigation-icon-color', newTheme.navigationIconColor);
      root.style.setProperty('--navigation-menu-text', newTheme.navigationMenuText);
      root.style.setProperty('--header-bg', newTheme.headerBg);
      root.style.setProperty('--header-text', newTheme.headerText);
      root.style.setProperty('--bg', newTheme.bg);
      root.style.setProperty('--card-bg', newTheme.cardBg);
      root.style.setProperty('--border-color', newTheme.borderColor);
      root.style.setProperty('--text-color', newTheme.textColor);
      root.style.setProperty('--text-secondary', newTheme.textSecondary);
      root.style.setProperty('--primary', newTheme.primary);
      root.style.setProperty('--secondary', newTheme.secondary);
      root.style.setProperty('--success', newTheme.success);
      root.style.setProperty('--error', newTheme.error);
      root.style.setProperty('--warning', newTheme.warning);
      root.style.setProperty('--info', newTheme.info);
      root.style.setProperty('--gray', newTheme.gray);
      
      // Also set navigation-specific variables
      root.style.setProperty('--sidebar-bg', newTheme.navigationBg);
      root.style.setProperty('--sidebar-border', newTheme.borderColor);
      root.style.setProperty('--accent', newTheme.navigationAccent);
      root.style.setProperty('--text', newTheme.navigationText);
      root.style.setProperty('--sidebar-width', '280px');
      root.style.setProperty('--sidebar-collapsed-width', '80px');
      
      console.log('Navigation bg set to:', newTheme.navigationBg);
      console.log('Sidebar bg variable:', getComputedStyle(root).getPropertyValue('--sidebar-bg'));
      
      // Save to localStorage for persistence
      localStorage.setItem('theme', newIsDark ? 'dark' : 'light');
      
      setIsDarkMode(newIsDark);
      
      // Update data-theme attribute on both html and body
      document.documentElement.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
      document.body.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
    }
  };

  return (
    <div className="theme-toggle">
      <button
        className="theme-toggle-button"
        onClick={handleToggle}
        aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <div className="theme-toggle-track">
          <div className={`theme-toggle-thumb ${isDarkMode ? 'dark' : 'light'}`}>
            {isDarkMode ? (
              <svg className="theme-icon moon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            ) : (
              <svg className="theme-icon sun" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"/>
              </svg>
            )}
          </div>
        </div>
      </button>
    </div>
  );
};

export default ThemeToggle;
